import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Clock, Play, Pause, RotateCcw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface TeamData {
  team_id: number;
  team_name: string;
  team_logo?: string;
  score: number;
}

interface GameScoreboardProps {
  gameId: string;
  homeTeam: TeamData;
  awayTeam: TeamData;
  sportName: string;
  canEdit?: boolean;
  showDetailed?: boolean;
}

export const GameScoreboard: React.FC<GameScoreboardProps> = ({
  gameId,
  homeTeam,
  awayTeam,
  sportName,
  canEdit = false,
  showDetailed = false
}) => {
  const [homeScore, setHomeScore] = useState(homeTeam.score || 0);
  const [awayScore, setAwayScore] = useState(awayTeam.score || 0);
  const [gameTime, setGameTime] = useState({ minutes: 0, seconds: 0 });
  const [isRunning, setIsRunning] = useState(false);
  const [period, setPeriod] = useState(1);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (isRunning) {
      interval = setInterval(() => {
        setGameTime(prev => {
          const newSeconds = prev.seconds + 1;
          if (newSeconds >= 60) {
            return { minutes: prev.minutes + 1, seconds: 0 };
          }
          return { ...prev, seconds: newSeconds };
        });
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [isRunning]);

  const updateScore = async (team: 'home' | 'away', points: number) => {
    if (!canEdit) return;

    const newHomeScore = team === 'home' ? homeScore + points : homeScore;
    const newAwayScore = team === 'away' ? awayScore + points : awayScore;

    try {
      // Update in database if needed
      setHomeScore(newHomeScore);
      setAwayScore(newAwayScore);
      
      toast.success(`Score updated: ${homeTeam.team_name} ${newHomeScore} - ${awayTeam.team_name} ${newAwayScore}`);
    } catch (error) {
      console.error('Error updating score:', error);
      toast.error('Failed to update score');
    }
  };

  const resetGame = () => {
    setHomeScore(0);
    setAwayScore(0);
    setGameTime({ minutes: 0, seconds: 0 });
    setIsRunning(false);
    setPeriod(1);
  };

  const formatTime = (time: { minutes: number; seconds: number }) => {
    const mins = time.minutes.toString().padStart(2, '0');
    const secs = time.seconds.toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };

  const getSportPeriodName = () => {
    switch (sportName.toLowerCase()) {
      case 'football':
        return period > 4 ? 'OT' : `Q${period}`;
      case 'basketball':
        return period > 4 ? 'OT' : `Q${period}`;
      case 'soccer':
        return period === 1 ? '1st Half' : period === 2 ? '2nd Half' : 'ET';
      default:
        return `Period ${period}`;
    }
  };

  if (!showDetailed) {
    // Simple scoreboard for roster view
    return (
      <div className="flex items-center justify-center space-x-8 mb-8">
        <div className="text-center">
          <div className="flex items-center space-x-4 mb-2">
            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-green-500">
              {homeTeam.team_logo ? (
                <img src={homeTeam.team_logo} alt="Home Team" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-green-600 flex items-center justify-center text-xs text-white font-bold">
                  {homeTeam.team_name.charAt(0)}
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="text-white font-bold text-sm">{homeTeam.team_name}</div>
              <div className="text-4xl font-bold text-green-400">{homeScore}</div>
            </div>
          </div>
        </div>

        <div className="text-center text-white">
          <div className="text-6xl font-bold mb-2">VS</div>
          <Badge variant="secondary" className="text-xs">
            {sportName}
          </Badge>
        </div>

        <div className="text-center">
          <div className="flex items-center space-x-4 mb-2">
            <div className="text-left">
              <div className="text-white font-bold text-sm">{awayTeam.team_name}</div>
              <div className="text-4xl font-bold text-blue-400">{awayScore}</div>
            </div>
            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-blue-500">
              {awayTeam.team_logo ? (
                <img src={awayTeam.team_logo} alt="Away Team" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-blue-600 flex items-center justify-center text-xs text-white font-bold">
                  {awayTeam.team_name.charAt(0)}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Card className="bg-gray-900 border-gray-700 mb-8">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">Live Scoreboard</h2>
          <Badge variant="outline" className="text-white border-white">
            {sportName}
          </Badge>
        </div>

        {/* Main Score Display */}
        <div className="grid grid-cols-3 gap-8 items-center mb-6">
          {/* Home Team */}
          <div className="text-center">
            <div className="flex items-center justify-center space-x-4 mb-4">
              <div className="w-16 h-16 rounded-full overflow-hidden border-4 border-green-500">
                {homeTeam.team_logo ? (
                  <img src={homeTeam.team_logo} alt="Home Team" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-green-600 flex items-center justify-center text-white font-bold">
                    {homeTeam.team_name.charAt(0)}
                  </div>
                )}
              </div>
              <div>
                <div className="text-white font-bold text-lg">{homeTeam.team_name}</div>
                <Badge className="bg-green-600 text-white">HOME</Badge>
              </div>
            </div>
            <div className="text-6xl font-bold text-green-400 mb-4">{homeScore}</div>
            {canEdit && (
              <div className="flex justify-center space-x-2">
                <Button size="sm" onClick={() => updateScore('home', 1)} className="bg-green-600 hover:bg-green-700">
                  +1
                </Button>
                <Button size="sm" onClick={() => updateScore('home', 2)} className="bg-green-600 hover:bg-green-700">
                  +2
                </Button>
                <Button size="sm" onClick={() => updateScore('home', 3)} className="bg-green-600 hover:bg-green-700">
                  +3
                </Button>
                <Button size="sm" variant="outline" onClick={() => updateScore('home', -1)}>
                  -1
                </Button>
              </div>
            )}
          </div>

          {/* Game Info */}
          <div className="text-center space-y-4">
            <div className="text-white">
              <div className="text-4xl font-bold mb-2">{formatTime(gameTime)}</div>
              <div className="text-lg">{getSportPeriodName()}</div>
            </div>
            
            {canEdit && (
              <div className="flex justify-center space-x-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setIsRunning(!isRunning)}
                  className="text-white border-white"
                >
                  {isRunning ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={resetGame}
                  className="text-white border-white"
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPeriod(period + 1)}
                  className="text-white border-white"
                >
                  Next Period
                </Button>
              </div>
            )}
          </div>

          {/* Away Team */}
          <div className="text-center">
            <div className="flex items-center justify-center space-x-4 mb-4">
              <div>
                <div className="text-white font-bold text-lg">{awayTeam.team_name}</div>
                <Badge className="bg-blue-600 text-white">AWAY</Badge>
              </div>
              <div className="w-16 h-16 rounded-full overflow-hidden border-4 border-blue-500">
                {awayTeam.team_logo ? (
                  <img src={awayTeam.team_logo} alt="Away Team" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-blue-600 flex items-center justify-center text-white font-bold">
                    {awayTeam.team_name.charAt(0)}
                  </div>
                )}
              </div>
            </div>
            <div className="text-6xl font-bold text-blue-400 mb-4">{awayScore}</div>
            {canEdit && (
              <div className="flex justify-center space-x-2">
                <Button size="sm" onClick={() => updateScore('away', 1)} className="bg-blue-600 hover:bg-blue-700">
                  +1
                </Button>
                <Button size="sm" onClick={() => updateScore('away', 2)} className="bg-blue-600 hover:bg-blue-700">
                  +2
                </Button>
                <Button size="sm" onClick={() => updateScore('away', 3)} className="bg-blue-600 hover:bg-blue-700">
                  +3
                </Button>
                <Button size="sm" variant="outline" onClick={() => updateScore('away', -1)}>
                  -1
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};